import React, { useState, useEffect, useCallback } from 'react';
import {
  Heart, RefreshCw, Camera, Clock, HardDrive, Wifi, WifiOff,
  CheckCircle, AlertTriangle, XCircle, Pause, Filter, X,
  ChevronRight, Server, History, Activity, Zap, Power, Video
} from 'lucide-react';
import api from '../services/api';

const SEVERITY_CONFIG = {
  ok:       { color: '#10b981', bg: '#d1fae5', icon: CheckCircle,    label: 'OK' },
  warning:  { color: '#f59e0b', bg: '#fef3c7', icon: AlertTriangle,  label: 'Aviso' },
  critical: { color: '#ef4444', bg: '#fee2e2', icon: XCircle,        label: 'Crítico' },
  offline:  { color: '#6b7280', bg: '#e5e7eb', icon: WifiOff,        label: 'Offline' },
  unknown:  { color: '#9ca3af', bg: '#f3f4f6', icon: Pause,          label: 'Sin datos' },
};


const Health = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <Heart size={28} color="#ef4444" /> Salud de la Flota
          </h1>
          <p style={styles.subtitle}>Estado y configuración automática de DVRs</p>
        </div>
      </div>

      <div style={styles.tabs}>
        <TabBtn
          active={activeTab === 'dashboard'}
          onClick={() => setActiveTab('dashboard')}
          icon={<Activity size={16}/>}
          label="Dashboard"
        />
        <TabBtn
          active={activeTab === 'provisioning'}
          onClick={() => setActiveTab('provisioning')}
          icon={<History size={16}/>}
          label="Provisioning Log"
        />
      </div>

      {activeTab === 'dashboard'   && <DashboardTab/>}
      {activeTab === 'provisioning' && <ProvisioningTab/>}
    </div>
  );
};


const TabBtn = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      ...styles.tabBtn,
      ...(active ? styles.tabBtnActive : {})
    }}
  >
    {icon} {label}
  </button>
);


// ============================================================================
// TAB 1: DASHBOARD
// ============================================================================
const DashboardTab = () => {
  const [devices, setDevices]             = useState([]);
  const [stats, setStats]                 = useState({});
  const [loading, setLoading]             = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterSucursal, setFilterSucursal] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [autoRefresh, setAutoRefresh]     = useState(true);
  const [lastRefresh, setLastRefresh]     = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterSucursal) params.append('sucursal', filterSucursal);

      const res = await api.get(`/api/health/dashboard?${params}`);
      if (res.data.success) {
        setDevices(res.data.devices || []);
        setStats(res.data.stats || {});
        setLastRefresh(new Date());
      }
    } catch (e) {
      console.error('Error loading health dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterSucursal]);

  const [checking, setChecking] = useState(false);
  const runCheckNow = useCallback(async () => {
    setChecking(true);
    try {
      await api.post('/api/health/run-now');
    } catch (e) {
      console.error('Error running health check:', e);
    } finally {
      await loadDashboard();
      setChecking(false);
    }
  }, [loadDashboard]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (!autoRefresh) return;
    const it = setInterval(loadDashboard, 30000);
    return () => clearInterval(it);
  }, [autoRefresh, loadDashboard]);

  const sucursales = [...new Set(devices.map(d => d.sucursal).filter(Boolean))].sort();

  return (
    <>
      <div style={styles.toolbar}>
        {lastRefresh && (
          <span style={styles.lastRefresh}>
            Actualizado: {lastRefresh.toLocaleTimeString()}
          </span>
        )}
        <label style={styles.toggle}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={e => setAutoRefresh(e.target.checked)}
            style={{marginRight: 6}}
          />
          Auto 30s
        </label>
        <button onClick={loadDashboard} style={styles.btnGhost}>
          <RefreshCw size={16}/> Refrescar
        </button>
        <button onClick={runCheckNow}
                style={{ ...styles.btnPrimary, opacity: checking ? 0.6 : 1, cursor: checking ? 'wait' : 'pointer' }}
                disabled={checking}>
          <RefreshCw size={16} className={checking ? 'spin' : ''}/>
          {checking ? ' Chequeando…' : ' Chequear ahora'}
        </button>
      </div>

      <StatsBar stats={stats} active={filterSeverity} onFilterChange={setFilterSeverity}/>

      {sucursales.length > 0 && (
        <Filters
          sucursales={sucursales}
          filterSucursal={filterSucursal}
          setFilterSucursal={setFilterSucursal}
          filterSeverity={filterSeverity}
          setFilterSeverity={setFilterSeverity}
        />
      )}

      {loading ? (
        <div style={styles.center}>Cargando...</div>
      ) : devices.length === 0 ? (
        <div style={styles.empty}>
          <Server size={48} color="#9ca3af"/>
          <p>{filterSeverity || filterSucursal
            ? 'Sin DVRs que coincidan con los filtros'
            : 'Sin datos de salud aún. El agente debe ejecutar al menos 1 ciclo.'}</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {devices.map(d => (
            <DVRCard key={d.device_id} device={d} onClick={() => setSelectedDevice(d)} />
          ))}
        </div>
      )}

      {selectedDevice && (
        <DetailModal
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onAction={loadDashboard}
        />
      )}
    </>
  );
};


// ============================================================================
// TAB 2: PROVISIONING LOG
// ============================================================================
const ProvisioningTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterSucursal, setFilterSucursal] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', 100);
      const res = await api.get(`/api/health/provision-log?${params}`);
      if (res.data.success) {
        setEntries(res.data.entries || []);
      }
    } catch (e) {
      console.error('Error loading provisioning log:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const it = setInterval(load, 30000);
    return () => clearInterval(it);
  }, [autoRefresh, load]);

  const sucursales = [...new Set(entries.map(e => e.sucursal_name).filter(Boolean))].sort();
  const filteredEntries = filterSucursal
    ? entries.filter(e => e.sucursal_name === filterSucursal)
    : entries;

  // Stats
  const totalProvisioned = entries.length;
  const todayCount = entries.filter(e => {
    const d = new Date(e.timestamp);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const isupCount = entries.filter(e => e.action_isup_configured).length;
  const sshCount = entries.filter(e => e.action_ssh_enabled).length;

  return (
    <>
      <div style={styles.toolbar}>
        <label style={styles.toggle}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={e => setAutoRefresh(e.target.checked)}
            style={{marginRight: 6}}
          />
          Auto 30s
        </label>
        <button onClick={load} style={styles.btnPrimary}>
          <RefreshCw size={16}/> Refrescar
        </button>
      </div>

      {/* Stats provisioning */}
      <div style={styles.statsBar}>
        <ProvStat label="Total"        count={totalProvisioned} icon={<History size={20}/>}        color="#6b7280"/>
        <ProvStat label="Hoy"          count={todayCount}       icon={<Zap size={20}/>}            color="#3b82f6"/>
        <ProvStat label="ISUP config." count={isupCount}        icon={<Server size={20}/>}         color="#10b981"/>
        <ProvStat label="SSH enable"   count={sshCount}         icon={<CheckCircle size={20}/>}    color="#8b5cf6"/>
      </div>

      {sucursales.length > 0 && (
        <div style={styles.filters}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <Filter size={16} color="#6b7280"/>
            <span style={{fontSize: 14, color: '#6b7280'}}>Sucursal:</span>
            <select
              value={filterSucursal}
              onChange={e => setFilterSucursal(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas</option>
              {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {filterSucursal && (
            <button onClick={() => setFilterSucursal('')} style={styles.btnLink}>
              <X size={14}/> Limpiar
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div style={styles.center}>Cargando...</div>
      ) : filteredEntries.length === 0 ? (
        <div style={styles.empty}>
          <History size={48} color="#9ca3af"/>
          <p>Sin provisionings aún</p>
          <p style={{fontSize: 13, color: '#9ca3af', marginTop: 4}}>
            Cuando el agente provisione un DVR nuevo, aparecerá aquí
          </p>
        </div>
      ) : (
        <div style={styles.provGrid}>
          {filteredEntries.map(entry => <ProvisioningCard key={entry.id} entry={entry}/>)}
        </div>
      )}
    </>
  );
};


const ProvStat = ({ label, count, icon, color }) => (
  <div style={{...styles.statBtn, background: 'white', cursor: 'default', borderColor: '#e5e7eb'}}>
    <div style={{...styles.severityBadge, background: `${color}15`, color}}>
      {icon}
    </div>
    <div style={{textAlign: 'left'}}>
      <div style={styles.statCount}>{count}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  </div>
);


const ProvisioningCard = ({ entry }) => {
  const ts = new Date(entry.timestamp);
  const minutesAgo = Math.floor((Date.now() - ts) / 60000);
  
  const timeAgo = minutesAgo < 1 ? 'Ahora' :
                  minutesAgo < 60 ? `Hace ${minutesAgo}m` :
                  minutesAgo < 1440 ? `Hace ${Math.floor(minutesAgo/60)}h` :
                  `Hace ${Math.floor(minutesAgo/1440)}d`;

  return (
    <div style={styles.provCard}>
      <div style={{...styles.provStripe, background: entry.success ? '#10b981' : '#ef4444'}}/>
      
      <div style={styles.provHeader}>
        <div>
          <div style={styles.provDeviceName}>
            {entry.device_id || entry.serial_number || 'Sin ID'}
          </div>
          <div style={styles.provMeta}>
            {entry.model || 'Modelo desconocido'}
          </div>
        </div>
        <div style={styles.provTime}>
          {timeAgo}
        </div>
      </div>

      <div style={styles.provDetails}>
        <div style={styles.provDetailRow}>
          <span style={styles.provDetailLabel}>📍 IP:</span>
          <span style={styles.provDetailValue}>{entry.ip_address || '—'}</span>
        </div>
        {entry.sucursal_name && (
          <div style={styles.provDetailRow}>
            <span style={styles.provDetailLabel}>🏢 Sucursal:</span>
            <span style={styles.provDetailValue}>{entry.sucursal_name}</span>
          </div>
        )}
        <div style={styles.provDetailRow}>
          <span style={styles.provDetailLabel}>🕐 Fecha:</span>
          <span style={styles.provDetailValue}>{ts.toLocaleString()}</span>
        </div>
      </div>

      <div style={styles.provActions}>
        {entry.action_isup_configured && (
          <ProvAction
            icon={<Server size={14}/>}
            label="ISUP configurado"
            detail={entry.prev_isup_ip ? `Era: ${entry.prev_isup_ip}:${entry.prev_isup_port}` : null}
            color="#10b981"
          />
        )}
        {entry.action_ssh_enabled && (
          <ProvAction
            icon={<CheckCircle size={14}/>}
            label="SSH habilitado"
            color="#8b5cf6"
          />
        )}
        {entry.action_rebooted && (
          <ProvAction
            icon={<Power size={14}/>}
            label="Reboot enviado"
            color="#3b82f6"
          />
        )}
      </div>

      {!entry.success && entry.error_message && (
        <div style={styles.provError}>⚠️ {entry.error_message}</div>
      )}
    </div>
  );
};


const ProvAction = ({ icon, label, detail, color }) => (
  <div style={{...styles.provActionBadge, background: `${color}15`, color}}>
    {icon} <span>{label}</span>
    {detail && <span style={styles.provActionDetail}>· {detail}</span>}
  </div>
);


// ============================================================================
// Stats Bar (dashboard)
// ============================================================================
const StatsBar = ({ stats, active, onFilterChange }) => {
  const items = [
    { key: '',         label: 'Total',     count: stats.total ?? 0,    color: '#6b7280' },
    { key: 'ok',       label: 'OK',        count: stats.ok ?? 0,       color: SEVERITY_CONFIG.ok.color },
    { key: 'warning',  label: 'Aviso',     count: stats.warning ?? 0,  color: SEVERITY_CONFIG.warning.color },
    { key: 'critical', label: 'Crítico',   count: stats.critical ?? 0, color: SEVERITY_CONFIG.critical.color },
    { key: 'offline',  label: 'Offline',   count: stats.offline ?? 0,  color: SEVERITY_CONFIG.offline.color },
    { key: 'unknown',  label: 'Sin datos', count: stats.unknown ?? 0,  color: SEVERITY_CONFIG.unknown.color },
  ];

  return (
    <div style={styles.statsBar}>
      {items.map(it => (
        <button
          key={it.label}
          onClick={() => onFilterChange(active === it.key ? '' : it.key)}
          style={{
            ...styles.statBtn,
            borderColor: active === it.key ? it.color : '#e5e7eb',
            background:  active === it.key ? `${it.color}15` : 'white',
          }}
        >
          <div style={{...styles.statDot, background: it.color}}/>
          <div style={{textAlign: 'left'}}>
            <div style={styles.statCount}>{it.count}</div>
            <div style={styles.statLabel}>{it.label}</div>
          </div>
        </button>
      ))}
    </div>
  );
};


const Filters = ({ sucursales, filterSucursal, setFilterSucursal, filterSeverity, setFilterSeverity }) => (
  <div style={styles.filters}>
    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
      <Filter size={16} color="#6b7280"/>
      <span style={{fontSize: 14, color: '#6b7280'}}>Sucursal:</span>
      <select
        value={filterSucursal}
        onChange={e => setFilterSucursal(e.target.value)}
        style={styles.select}
      >
        <option value="">Todas</option>
        {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
    {(filterSeverity || filterSucursal) && (
      <button
        onClick={() => { setFilterSeverity(''); setFilterSucursal(''); }}
        style={styles.btnLink}
      >
        <X size={14}/> Limpiar
      </button>
    )}
  </div>
);


const DVRCard = ({ device, onClick }) => {
  const sev = device.effective_severity || device.severity || 'unknown';
  const cfg = SEVERITY_CONFIG[sev];
  const Icon = cfg.icon;

  const isOffline = device.severity === 'offline' || !device.reachable;
  const isCriticalOffline = sev === 'critical' && isOffline;
  
  const minutesSinceCheck = device.last_health_check
    ? Math.floor((Date.now() - new Date(device.last_health_check)) / 60000)
    : null;

  return (
    <div onClick={onClick} style={styles.card}>
      <div style={{...styles.severityStripe, background: cfg.color}}/>
      <div style={styles.cardHeader}>
        <div style={{...styles.severityBadge, background: cfg.bg, color: cfg.color}}>
          {isCriticalOffline ? <WifiOff size={20}/> : <Icon size={20}/>}
        </div>
        <div style={styles.cardTitle}>
          <div style={styles.deviceName}>{device.device_name || device.device_id}</div>
          <div style={styles.deviceMeta}>{device.device_id}</div>
        </div>
        {device.critical_alerts_count > 0 && (
          <div style={styles.alertBadge}>{device.critical_alerts_count}</div>
        )}
        <ChevronRight size={18} color="#9ca3af"/>
      </div>

      {device.sucursal && (
        <div style={styles.sucursalRow}>📍 {device.sucursal}</div>
      )}

      {/* Banner: offline pero crítico */}
      {isCriticalOffline && (
        <div style={styles.criticalOfflineBanner}>
          ⚠️ Crítico • Offline desde {device.last_seen_online
            ? formatTimeAgo(new Date(device.last_seen_online))
            : 'desconocido'}
        </div>
      )}

      {/* Banner: solo offline */}
      {isOffline && !isCriticalOffline && device.model && (
        <div style={styles.staleBanner}>
          🔌 Offline • Última conexión: {device.last_seen_online
            ? formatTimeAgo(new Date(device.last_seen_online))
            : 'desconocida'}
        </div>
      )}

      <div style={{...styles.metricsRow, opacity: isOffline ? 0.65 : 1}}>
        <Metric
          icon={<Camera size={14}/>}
          label="Grabando"
          value={`${device.channels_recording ?? '?'}/${device.channels_total ?? '?'}`}
          ok={device.is_recording_any && !isOffline}
        />
        <Metric
          icon={<Video size={14}/>}
          label="Señal"
          value={device.channels_with_signal != null
            ? `${device.channels_with_signal}/${device.channels_expected ?? device.channels_total ?? '?'}`
            : '?'}
          ok={!isOffline && device.channels_with_signal != null
              && device.channels_with_signal >= (device.channels_expected ?? device.channels_total ?? 0)}
          warning={!isOffline && device.channels_with_signal != null
              && device.channels_with_signal < (device.channels_expected ?? device.channels_total ?? 0)}
        />
        <Metric
          icon={<HardDrive size={14}/>}
          label="Disco"
          value={
            ['unformatted', 'error', 'faulty'].includes(device.storage_status) 
              ? (device.storage_status === 'unformatted' ? 'Sin format.' : 'Error') :
            device.storage_usage_percent != null
              ? `${Number(device.storage_usage_percent).toFixed(0)}%`
              : '?'
          }
          ok={!['unformatted', 'error', 'faulty'].includes(device.storage_status) && device.storage_status === 'ok' && !isOffline}
          critical={['unformatted', 'error', 'faulty'].includes(device.storage_status)}
        />
        <Metric
          icon={<Clock size={14}/>}
          label="Hora"
          value={
            device.time_drift_seconds != null
              ? formatDrift(device.time_drift_seconds)
              : '?'
          }
          warning={Math.abs(device.time_drift_seconds || 0) > 60 && !isOffline}
        />
      </div>

      {device.issues_summary && (
        <div style={styles.issuesSummary}>
          {device.issues_summary.length > 100
            ? device.issues_summary.substring(0, 100) + '...'
            : device.issues_summary}
        </div>
      )}

      <div style={styles.cardFooter}>
        <span>FW {device.firmware_version || '?'}</span>
        <span>{minutesSinceCheck !== null ? `Hace ${minutesSinceCheck}m` : 'Sin check'}</span>
      </div>
    </div>
  );
};


const Metric = ({ icon, label, value, ok, warning, critical }) => {
  let color = '#1f2937';
  if (critical) color = SEVERITY_CONFIG.critical.color;
  else if (warning) color = SEVERITY_CONFIG.warning.color;
  else if (ok) color = SEVERITY_CONFIG.ok.color;

  return (
    <div style={styles.metric}>
      <div style={styles.metricLabel}>{icon} {label}</div>
      <div style={{...styles.metricValue, color}}>{value}</div>
    </div>
  );
};


const formatDrift = (seconds) => {
  const abs = Math.abs(seconds);
  if (abs < 60) return `${seconds}s`;
  if (abs < 3600) return `${Math.floor(seconds/60)}m`;
  return `${Math.floor(seconds/3600)}h`;
};


const formatTimeAgo = (date) => {
  const minutes = Math.floor((Date.now() - date) / 60000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return `hace ${minutes}m`;
  if (minutes < 1440) return `hace ${Math.floor(minutes/60)}h`;
  return `hace ${Math.floor(minutes/1440)}d`;
};


const DetailModal = ({ device, onClose, onAction }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    api.get(`/api/health/${device.device_id}`)
      .then(r => setDetail(r.data))
      .catch(e => setDetail({error: e.response?.data?.detail || 'No se pudo cargar'}))
      .finally(() => setLoading(false));
  }, [device.device_id]);

  const performAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionLoading(true);
    setActionMsg('Ejecutando...');
    try {
      const res = await api.post(`/api/health/${device.device_id}/${action}`);
      // Cuando falla, lo que importa es lo que contestó el equipo: "la acción
      // falló" a secas no deja nada que investigar.
      setActionMsg(res.data.success
        ? '✅ Acción aplicada correctamente'
        : '❌ La acción falló' + (res.data.response ? `: ${res.data.response}` : ''));
      onAction();
    } catch (e) {
      setActionMsg('❌ Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{margin: 0, fontSize: 20}}>{device.device_name || device.device_id}</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={20}/></button>
        </div>

        {loading ? (
          <div style={{padding: 40, textAlign: 'center'}}>Cargando...</div>
        ) : detail?.error ? (
          <div style={{padding: 40, color: '#ef4444', textAlign: 'center'}}>{detail.error}</div>
        ) : (
          <>
            <Section title="Información del dispositivo">
              <KVGrid items={[
                ['Modelo', detail.model],
                ['Firmware', detail.firmware_version],
                ['Serial', detail.serial_number],
                ['MAC', detail.mac_address],
                ['IP local', detail.local_ip],
                ['Sucursal', detail.lan_mapping?.sucursal_name],
              ]}/>
            </Section>

            <Section title="Hora & NTP">
              <KVGrid items={[
                ['Hora DVR', detail.device_datetime ? new Date(detail.device_datetime).toLocaleString() : '—'],
                ['Desfase', detail.time_drift_seconds != null ? formatDrift(detail.time_drift_seconds) : '—'],
                ['Modo', detail.time_mode],
                ['NTP configurado', detail.ntp_configured ? 'Sí' : 'No'],
              ]}/>
              <div style={styles.actions}>
                <button onClick={() => performAction('sync-time')} style={styles.btnAction} disabled={actionLoading}>
                  <Clock size={14}/> Sincronizar hora
                </button>
                <button onClick={() => performAction('configure-ntp')} style={styles.btnAction} disabled={actionLoading}>
                  <Wifi size={14}/> Configurar NTP
                </button>
              </div>
            </Section>

            <Section title="Storage">
              <KVGrid items={[
                ['Estado', detail.storage_status],
                ['Capacidad', detail.storage_capacity_gb ? `${detail.storage_capacity_gb} GB` : '—'],
                ['Uso', detail.storage_usage_percent != null ? `${detail.storage_usage_percent}%` : '—'],
              ]}/>
              {detail.storage_status === 'unformatted' && (
                <div style={styles.actions}>
                  <button
                    onClick={() => performAction('format-storage', '⚠️ Formatear borrará todo. ¿Continuar?')}
                    style={{...styles.btnAction, background: '#ef4444', color: 'white', borderColor: '#dc2626'}}
                    disabled={actionLoading}
                  >
                    <HardDrive size={14}/> Formatear SD
                  </button>
                </div>
              )}
            </Section>

            <Section title={`Canales (${detail.channels?.length || 0})`}>
              <div style={styles.channelGrid}>
                {(detail.channels || []).map(ch => (
                  <div key={ch.channel} style={styles.channelCard}>
                    <div style={styles.channelHeader}>
                      <span style={{fontWeight: 600}}>Ch {ch.channel}</span>
                      <span style={{
                        color: ch.is_recording ? SEVERITY_CONFIG.ok.color : SEVERITY_CONFIG.critical.color,
                        fontSize: 12,
                      }}>
                        {ch.is_recording ? '● REC' : '○ Off'}
                      </span>
                    </div>
                    {ch.resolution && (
                      <div style={styles.channelMeta}>
                        {ch.resolution} @ {ch.framerate}fps {ch.bitrate_kbps}kbps
                      </div>
                    )}
                    {ch.camera_name && (
                      <div style={styles.channelName}>{ch.camera_name}</div>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {detail.active_alerts?.length > 0 && (
              <Section title={`Alertas activas (${detail.active_alerts.length})`}>
                {detail.active_alerts.map(a => {
                  const acfg = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.unknown;
                  return (
                    <div key={a.id} style={{...styles.alert, borderLeftColor: acfg.color}}>
                      <div style={styles.alertHeader}>
                        <span style={styles.alertType}>{a.alert_type}</span>
                        <span style={{...styles.alertSev, background: acfg.bg, color: acfg.color}}>
                          {acfg.label}
                        </span>
                      </div>
                      <div>{a.message}</div>
                      <div style={styles.alertMeta}>
                        Desde: {new Date(a.first_detected).toLocaleString()} • Veces: {a.occurrences}
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}

            {actionMsg && (
              <div style={styles.actionResult}>{actionMsg}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


const Section = ({ title, children }) => (
  <div style={styles.modalSection}>
    <h3 style={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);


const KVGrid = ({ items }) => (
  <div style={styles.kvGrid}>
    {items.map(([label, value]) => (
      <div key={label}>
        <div style={styles.kvLabel}>{label}</div>
        <div style={styles.kvValue}>{value || '—'}</div>
      </div>
    ))}
  </div>
);


// ============================================================================
// Styles
// ============================================================================
const styles = {
  container: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 28, margin: 0, fontWeight: 700, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 12 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '6px 0 0 40px' },

  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
  tabBtn: { padding: '10px 18px', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 14, color: '#6b7280', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 },
  tabBtnActive: { color: '#3b82f6', borderBottomColor: '#3b82f6', fontWeight: 600 },

  toolbar: { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16, flexWrap: 'wrap' },
  lastRefresh: { fontSize: 12, color: '#9ca3af', marginRight: 'auto' },
  toggle: { fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', cursor: 'pointer' },

  statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 },
  statBtn: { display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', transition: 'all .15s', background: 'white' },
  statDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
  statCount: { fontSize: 24, fontWeight: 700, lineHeight: 1, color: '#1f2937' },
  statLabel: { fontSize: 12, color: '#6b7280', marginTop: 4 },

  filters: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' },
  select: { padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, background: 'white' },

  center: { textAlign: 'center', padding: 60, color: '#6b7280' },
  empty: { textAlign: 'center', padding: 60, color: '#9ca3af', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, cursor: 'pointer', transition: 'all .15s', position: 'relative', overflow: 'hidden' },
  severityStripe: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  severityBadge: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { flex: 1, minWidth: 0 },
  deviceName: { fontWeight: 600, fontSize: 16, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deviceMeta: { fontSize: 12, color: '#6b7280', fontFamily: 'monospace' },
  alertBadge: { background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  sucursalRow: { fontSize: 13, color: '#6b7280', marginBottom: 12 },
  staleBanner: { fontSize: 12, color: '#6b7280', background: '#f3f4f6', borderRadius: 6, padding: '6px 10px', marginBottom: 8, border: '1px dashed #d1d5db' },
  criticalOfflineBanner: { fontSize: 12, color: '#991b1b', background: '#fef2f2', borderRadius: 6, padding: '6px 10px', marginBottom: 8, border: '1px solid #fecaca', fontWeight: 500 },

  metricsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 },
  metric: { padding: '10px 8px', background: '#f9fafb', borderRadius: 6, textAlign: 'center' },
  metricLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 },
  metricValue: { fontSize: 18, fontWeight: 700, marginTop: 4 },

  issuesSummary: { fontSize: 13, color: '#991b1b', background: '#fef2f2', borderRadius: 6, padding: '8px 12px', margin: '8px 0', border: '1px solid #fecaca' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', paddingTop: 8, borderTop: '1px solid #f3f4f6', marginTop: 8 },

  // Provisioning
  provGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  provCard: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, position: 'relative', overflow: 'hidden' },
  provStripe: { position: 'absolute', top: 0, left: 0, bottom: 0, width: 4 },
  provHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginLeft: 8, marginBottom: 12 },
  provDeviceName: { fontWeight: 600, fontSize: 15, color: '#1f2937', fontFamily: 'monospace' },
  provMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  provTime: { fontSize: 12, color: '#9ca3af' },
  provDetails: { marginLeft: 8, marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 },
  provDetailRow: { fontSize: 13 },
  provDetailLabel: { color: '#6b7280', marginRight: 6 },
  provDetailValue: { color: '#1f2937', fontWeight: 500 },
  provActions: { marginLeft: 8, display: 'flex', gap: 8, flexWrap: 'wrap' },
  provActionBadge: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 },
  provActionDetail: { fontSize: 11, opacity: 0.7, fontFamily: 'monospace' },
  provError: { marginTop: 8, marginLeft: 8, padding: '8px 12px', background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 },

  // Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  modal: { background: 'white', borderRadius: 12, width: '100%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280' },
  modalSection: { padding: '16px 20px', borderBottom: '1px solid #f3f4f6' },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 10, marginTop: 0 },
  kvGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  kvLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 },
  kvValue: { fontSize: 14, fontWeight: 500, color: '#1f2937', wordBreak: 'break-word' },

  channelGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 },
  channelCard: { background: '#f9fafb', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' },
  channelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  channelMeta: { fontSize: 11, color: '#6b7280', marginTop: 4 },
  channelName: { fontSize: 12, color: '#374151', marginTop: 2 },

  alert: { borderLeft: '3px solid #6b7280', background: '#f9fafb', padding: '10px 12px', marginBottom: 8, borderRadius: 4 },
  alertHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  alertType: { fontWeight: 600, fontSize: 12, color: '#374151', fontFamily: 'monospace' },
  alertSev: { padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  alertMeta: { fontSize: 11, color: '#9ca3af', marginTop: 6 },

  actions: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  actionResult: { padding: 14, background: '#eff6ff', borderTop: '1px solid #bfdbfe', textAlign: 'center', fontSize: 14 },

  btnPrimary: { padding: '8px 14px', borderRadius: 6, background: '#3b82f6', color: 'white', border: '1px solid #2563eb', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  btnGhost: { padding: '8px 14px', borderRadius: 6, background: '#fff', color: '#334155', border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  btnAction: { padding: '8px 12px', borderRadius: 6, background: 'white', color: '#374151', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  btnLink: { padding: '4px 10px', background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 },
};

export default Health;